import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useCreateQuizMutation } from "../../slices/quizApiSlice";
import { useGetQuestionBankSubjectsQuery } from "../../slices/questionBankSubjectApiSlice";

const CreateQuizScreen = () => {
  const navigate = useNavigate();

  const [subject, setSubject] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [durationMinutes, setDurationMinutes] = useState(30);
  const [passingPercentage, setPassingPercentage] = useState(40);

  const [allowMultipleAttempts, setAllowMultipleAttempts] = useState(false);

  const [maxAttempts, setMaxAttempts] = useState(1);

  const { data: subjects = [] } = useGetQuestionBankSubjectsQuery();

  const [createQuiz, { isLoading }] = useCreateQuizMutation();

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!subject) {
      toast.error("Please select a subject");
      return;
    }

    if (!name.trim()) {
      toast.error("Quiz name is required");
      return;
    }

    if (Number(durationMinutes) <= 0) {
      toast.error("Duration must be greater than zero");
      return;
    }

    if (Number(passingPercentage) < 0 || Number(passingPercentage) > 100) {
      toast.error("Passing percentage must be between 0 and 100");
      return;
    }

    try {
      const createdQuiz = await createQuiz({
        questionBankSubject: subject,

        name: name.trim(),

        description: description.trim(),

        durationMinutes: Number(durationMinutes),

        passingPercentage: Number(passingPercentage),

        allowMultipleAttempts,

        maxAttempts: allowMultipleAttempts ? Number(maxAttempts) : 1,
      }).unwrap();

      toast.success("Quiz created successfully");

      // Move to blueprint configuration
      navigate(`/quizzes/${createdQuiz._id}/blueprint/create`);
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Failed to create quiz");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/quizzes"
          className="inline-flex items-center gap-2 mb-6 rounded-xl bg-white px-4 py-2 shadow hover:bg-blue-50 text-blue-600"
        >
          ← Back to Quizzes
        </Link>

        <div className="rounded-3xl border bg-white/80 backdrop-blur-lg shadow-2xl p-6 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800">Create Quiz</h1>

            <p className="mt-2 text-gray-500">
              Create quiz details first. Configure sections and questions using
              Quiz Blueprint.
            </p>
          </div>

          {isLoading && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              Creating quiz...
            </div>
          )}

          <form
            onSubmit={submitHandler}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Subject */}

            <div>
              <label className="block mb-2 font-semibold">Subject *</label>

              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-xl border px-4 py-3"
              >
                <option value="">Select Subject</option>

                {subjects.map((sub) => (
                  <option key={sub._id} value={sub._id}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Name */}

            <div>
              <label className="block mb-2 font-semibold">Quiz Name *</label>

              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border px-4 py-3"
              />
            </div>

            {/* Duration */}

            <div>
              <label className="block mb-2 font-semibold">
                Duration Minutes
              </label>

              <input
                type="number"
                min="1"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                className="w-full rounded-xl border px-4 py-3"
              />
            </div>

            {/* Passing */}

            <div>
              <label className="block mb-2 font-semibold">
                Passing Percentage
              </label>

              <input
                type="number"
                min="0"
                max="100"
                value={passingPercentage}
                onChange={(e) => setPassingPercentage(e.target.value)}
                className="w-full rounded-xl border px-4 py-3"
              />
            </div>

            {/* Attempts */}

            <div>
              <label className="block mb-2 font-semibold">
                Maximum Attempts
              </label>

              <input
                type="number"
                min="1"
                disabled={!allowMultipleAttempts}
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(e.target.value)}
                className="w-full rounded-xl border px-4 py-3 disabled:bg-gray-200"
              />
            </div>

            {/* Description */}

            <div className="md:col-span-2">
              <label className="block mb-2 font-semibold">Description</label>

              <textarea
                rows="5"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-xl border px-4 py-3"
              />
            </div>

            {/* Options */}

            <div className="md:col-span-2">
              <label className="flex items-center gap-3 rounded-xl border p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowMultipleAttempts}
                  onChange={(e) => setAllowMultipleAttempts(e.target.checked)}
                />
                Allow Multiple Attempts
              </label>
            </div>

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-4 text-lg font-semibold text-white disabled:opacity-60"
              >
                {isLoading
                  ? "Creating..."
                  : "Create Quiz & Configure Blueprint"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateQuizScreen;
